import { api } from "./client";

/** Report returned by materialization — the figures the Phase A gate reads. */
export type CohortReport = {
  cohort_id: string;
  run_source_id: string;
  lead_count: number;
  url_count: number;
  no_url: number;
  unnormalizable: number;
  /** Leads whose scan target came from a contact record rather than
   * `lead.url`. A sudden 0 on a cohort that previously had many means the
   * `contacts` field stopped being requested — which silently costs those
   * leads their URL without changing any other figure here. */
  from_contact: number;
  shared_url_groups: number;
  shared_url_leads: number;
  max_leads_on_one_url: number;
};

export type Cohort = {
  id: string;
  name: string;
  query: string;
  created_at: string | null;
  lead_count: number;
  run_source_id: string | null;
  phase_b_run_id: string | null;
  reconcile_status: string | null;
  reconcile_error: string | null;
  /** Checks Phase B actually evaluated — a run launched without the
   *  extract checks would SKIP the whole cohort. */
  check_names: string[] | null;
};

export type CohortDetail = Cohort & {
  /** Outcome bucket → count. Keys are open-ended; render whatever comes back. */
  outcomes: Record<string, number>;
};

/** Count only — no cohort, no run source, no writes. */
export const previewCohort = (query: string) =>
  api<{ total: number }>("/api/close-cohorts/preview", {
    method: "POST",
    body: JSON.stringify({ query }),
  });

export const createCohort = (name: string, query: string) =>
  api<CohortReport>("/api/close-cohorts", {
    method: "POST",
    body: JSON.stringify({ name, query }),
  });

export const listCohorts = () => api<Cohort[]>("/api/close-cohorts");

export const getCohort = (id: string) =>
  api<CohortDetail>(`/api/close-cohorts/${id}`);

/**
 * Phase C. `dryRun` defaults to true on the server too — the UI never sends
 * a real reconcile the operator didn't explicitly ask for.
 */
export const reconcileCohort = (
  id: string,
  newStatusId: string,
  dryRun: boolean,
) =>
  api<{ cohort_id: string; started: boolean; dry_run: boolean }>(
    `/api/close-cohorts/${id}/reconcile`,
    {
      method: "POST",
      body: JSON.stringify({ new_status_id: newStatusId, dry_run: dryRun }),
    },
  );
